import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * ⓘ アイコンにぶら下がる補足。
 * タッチ端末にはホバーが無いので、ホバーだけでなくクリックでも開閉できるようにしている。
 * 中身はアイコンの右端に揃えて左へ広げる（カードの右上に置く前提）。
 */
export function InfoTooltip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  // クリックで開いたときは外側のクリックで閉じる
  useEffect(() => {
    if (!open) return
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <span
      ref={containerRef}
      className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        // ホバーで既に開いているところをクリックすると閉じてしまうので、
        // クリックは「開く」だけにする。閉じるのは外側のクリック・Esc・カーソルを外したとき
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute right-0 top-6 z-30 w-72 max-w-[calc(100vw-3rem)] rounded-lg border border-gray-200 bg-white p-3 text-left font-normal shadow-xl dark:border-gray-700 dark:bg-gray-800"
        >
          {children}
        </div>
      )}
    </span>
  )
}
